import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { WorkingProfile, PolicyViolationSettings, PolicyLimits, PolicyCheckType } from '@/types/timesheetSettings';
import { HelpCircle, Clock, ShieldAlert } from 'lucide-react';
import { PolicyCheckCard, type PolicyCheckConfig } from './PolicyCheckCard';

interface WorkingHoursConfigurationCompactProps {
  workingProfile: WorkingProfile;
  policyEnforcement: PolicyViolationSettings;
  policyLimits: PolicyLimits | undefined;
  onWorkingProfileChange: (profile: WorkingProfile) => void;
  onPolicyEnforcementChange: (enforcement: PolicyViolationSettings) => void;
  onPolicyLimitsChange: (limits: PolicyLimits | undefined) => void;
}

export function WorkingHoursConfigurationCompact({
  workingProfile,
  policyEnforcement,
  policyLimits,
  onWorkingProfileChange,
  onPolicyEnforcementChange,
  onPolicyLimitsChange,
}: WorkingHoursConfigurationCompactProps) {
  const [weeklyHoursInput, setWeeklyHoursInput] = React.useState<string>(workingProfile.standardHoursPerWeek.toFixed(2));
  const [dailyHoursInputs, setDailyHoursInputs] = React.useState<Record<string, string>>({});
  
  // State for policy check configuration
  const [excessiveHoursConfig, setExcessiveHoursConfig] = React.useState<PolicyCheckConfig>({
    enabled: true,
    threshold: policyEnforcement.monitoringFrequency === 'week' ? 2 : 1,
    severity: 'warning',
    enforcement: 'warn',
  });

  // State for missing hours alert configuration
  const [missingHoursConfig, setMissingHoursConfig] = React.useState<PolicyCheckConfig>({
    enabled: true,
    threshold: policyEnforcement.monitoringFrequency === 'week' ? 8 : 2,
    severity: 'info',
    enforcement: 'warn',
  });

  // State for non-working day entry configuration
  const [nonWorkingDayConfig, setNonWorkingDayConfig] = React.useState<PolicyCheckConfig>({
    enabled: true,
    threshold: 0, // Not used for this policy
    severity: 'info',
    enforcement: 'warn',
  });

  // State for legal maximum configuration
  const [legalMaximumConfig, setLegalMaximumConfig] = React.useState<PolicyCheckConfig>({
    enabled: false, // Default to unchecked/disabled
    threshold: policyEnforcement.monitoringFrequency === 'week' ? 48 : 13,
    severity: 'critical',
    enforcement: 'block',
  });

  // Update threshold when monitoring frequency changes
  React.useEffect(() => {
    setExcessiveHoursConfig(prev => ({
      ...prev,
      threshold: policyEnforcement.monitoringFrequency === 'week' ? 2 : 1,
    }));
    setMissingHoursConfig(prev => ({
      ...prev,
      threshold: policyEnforcement.monitoringFrequency === 'week' ? 8 : 2,
    }));
    setLegalMaximumConfig(prev => ({
      ...prev,
      threshold: policyEnforcement.monitoringFrequency === 'week' ? 48 : 13,
    }));
  }, [policyEnforcement.monitoringFrequency]);

  // Update local input when workingProfile changes externally
  React.useEffect(() => {
    setWeeklyHoursInput(workingProfile.standardHoursPerWeek.toFixed(2));
  }, [workingProfile.standardHoursPerWeek]);

  // Initialize daily hours inputs
  React.useEffect(() => {
    const inputs: Record<string, string> = {};
    Object.entries(workingProfile.workingDays).forEach(([key, dayData]) => {
      inputs[key] = dayData.expectedHours.toFixed(2);
    });
    setDailyHoursInputs(inputs);
  }, [workingProfile.workingDays]);

  // Calculate working days and daily hours
  const workingDays = Object.values(workingProfile.workingDays).filter(d => d.isWorkingDay);
  const avgDailyHours = workingDays.length > 0 
    ? workingProfile.standardHoursPerWeek / workingDays.length 
    : 8;

  // Quick preset handler (hours only)
  const applyQuickHours = (hours: number) => {
    // Get current working days
    const currentWorkingDays = Object.entries(workingProfile.workingDays)
      .filter(([_, dayData]) => dayData.isWorkingDay);
    
    if (currentWorkingDays.length === 0) {
      // If no working days, just update the weekly hours
      onWorkingProfileChange({
        ...workingProfile,
        standardHoursPerWeek: hours,
      });
      return;
    }

    // Calculate new daily hours
    const newDailyHours = hours / currentWorkingDays.length;
    
    // Update working days with new hours (only for non-custom days)
    const updatedWorkingDays = { ...workingProfile.workingDays };
    currentWorkingDays.forEach(([dayKey, dayData]) => {
      if (!dayData.isCustom) {
        updatedWorkingDays[dayKey as keyof typeof workingProfile.workingDays] = {
          ...dayData,
          expectedHours: newDailyHours,
        };
      }
    });

    onWorkingProfileChange({
      ...workingProfile,
      standardHoursPerWeek: hours,
      workingDays: updatedWorkingDays,
    });
  };

  // Quick preset handler with days
  const applyPreset = (hours: number, days: string[]) => {
    const dailyHours = hours / days.length;
    const workingDaysConfig: typeof workingProfile.workingDays = {
      monday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      tuesday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      wednesday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      thursday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      friday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      saturday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      sunday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
    };

    days.forEach((day) => {
      workingDaysConfig[day as keyof typeof workingDaysConfig] = {
        isWorkingDay: true,
        expectedHours: dailyHours,
        isCustom: false,
      };
    });

    onWorkingProfileChange({
      standardHoursPerWeek: hours,
      workingDays: workingDaysConfig,
    });
  };

  // Policy check handlers
  const toggleCheck = (checkType: PolicyCheckType, enabled: boolean) => {
    onPolicyEnforcementChange({
      ...policyEnforcement,
      checks: {
        ...policyEnforcement.checks,
        [checkType]: {
          ...policyEnforcement.checks[checkType],
          enabled,
        },
      },
      preset: 'custom',
    });
  };

  const updateThreshold = (checkType: PolicyCheckType, threshold: number) => {
    onPolicyEnforcementChange({
      ...policyEnforcement,
      checks: {
        ...policyEnforcement.checks,
        [checkType]: {
          ...policyEnforcement.checks[checkType],
          threshold,
        },
      },
      preset: 'custom',
    });
  };

  const updateSeverity = (checkType: PolicyCheckType, severity: 'info' | 'warning' | 'critical') => {
    onPolicyEnforcementChange({
      ...policyEnforcement,
      checks: {
        ...policyEnforcement.checks,
        [checkType]: {
          ...policyEnforcement.checks[checkType],
          severity,
        },
      },
      preset: 'custom',
    });
  };

  // Legal limits handlers
  const toggleLegalLimits = (enabled: boolean) => {
    if (enabled) {
      onPolicyLimitsChange({
        legalMaxHoursPerDay: 13,
        legalMaxHoursPerWeek: 48,
      });
    } else {
      onPolicyLimitsChange(undefined);
    }
  };

  const updateLegalLimit = (field: 'legalMaxHoursPerDay' | 'legalMaxHoursPerWeek', value: number) => {
    if (policyLimits) {
      onPolicyLimitsChange({
        ...policyLimits,
        [field]: value,
      });
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header with Icon and Subtitle */}
        <div className="flex items-start gap-3 pb-4 border-b">
          <div className="rounded-lg bg-primary/10 p-2.5">
            <Clock className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold">Working Hours</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Define standard working hours for your organisation
            </p>
          </div>
        </div>

        {/* Standard Hours - Unified Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Standard Hours</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Your organization's baseline working schedule. All policy checks use these hours as reference.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            
            {/* Quick Presets - Inline */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Presets:</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset(20, ['monday', 'tuesday', 'wednesday'])}
                className="h-7 px-2 text-xs"
              >
                20h
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset(30, ['monday', 'tuesday', 'wednesday', 'thursday'])}
                className="h-7 px-2 text-xs"
              >
                30h
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset(37.5, ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])}
                className="h-7 px-2 text-xs"
              >
                37.5h
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset(40, ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])}
                className="h-7 px-2 text-xs"
              >
                40h
              </Button>
            </div>
          </div>

          {/* Combined Weekly Hours and Days Grid */}
          <div className="p-4 bg-muted/20 rounded-lg border space-y-4">
            {/* Weekly Hours Input */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground min-w-[60px]">Total:</Label>
              <Input
                type="number"
                value={weeklyHoursInput}
                onChange={(e) => {
                  setWeeklyHoursInput(e.target.value);
                }}
                onBlur={(e) => {
                  // Apply changes on blur
                  const value = parseFloat(e.target.value) || 0;
                  const clampedValue = Math.max(0, Math.min(168, value));
                  applyQuickHours(clampedValue);
                }}
                onKeyDown={(e) => {
                  // Apply changes on Enter key
                  if (e.key === 'Enter') {
                    const value = parseFloat(weeklyHoursInput) || 0;
                    const clampedValue = Math.max(0, Math.min(168, value));
                    applyQuickHours(clampedValue);
                    e.currentTarget.blur();
                  }
                }}
                className="w-24 h-8"
                min="0"
                step="0.01"
              />
              <span className="text-xs text-muted-foreground">h/week</span>
            </div>

            {/* Days and Hours Grid */}
            <div className="grid grid-cols-7 gap-3">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                const dayKey = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][index] as keyof typeof workingProfile.workingDays;
                const dayData = workingProfile.workingDays[dayKey];
                
                return (
                  <div key={day} className="space-y-2">
                    <Label className="text-xs font-medium text-center block">{day}</Label>
                    <div className="flex items-center justify-center">
                      <Switch
                        checked={dayData.isWorkingDay}
                        onCheckedChange={(checked) => {
                          const updatedWorkingDays = {
                            ...workingProfile.workingDays,
                            [dayKey]: {
                              ...dayData,
                              isWorkingDay: checked,
                              expectedHours: checked ? (dayData.expectedHours || avgDailyHours) : 0,
                            },
                          };
                          
                          // Recalculate weekly total from all days
                          const newWeeklyTotal = Object.values(updatedWorkingDays)
                            .reduce((sum, day) => sum + (day.isWorkingDay ? day.expectedHours : 0), 0);
                          
                          onWorkingProfileChange({
                            ...workingProfile,
                            standardHoursPerWeek: newWeeklyTotal,
                            workingDays: updatedWorkingDays,
                          });
                        }}
                        className="scale-90"
                      />
                    </div>
                    <Input
                      type="number"
                      value={dailyHoursInputs[dayKey] || '0.00'}
                      onChange={(e) => {
                        setDailyHoursInputs({
                          ...dailyHoursInputs,
                          [dayKey]: e.target.value,
                        });
                      }}
                      onBlur={(e) => {
                        // Apply changes on blur
                        const value = parseFloat(e.target.value) || 0;
                        const clampedValue = Math.max(0, Math.min(24, value));
                        
                        const updatedWorkingDays = {
                          ...workingProfile.workingDays,
                          [dayKey]: {
                            ...dayData,
                            expectedHours: clampedValue,
                            isCustom: true,
                          },
                        };
                        
                        // Recalculate weekly total from all days
                        const newWeeklyTotal = Object.values(updatedWorkingDays)
                          .reduce((sum, day) => sum + (day.isWorkingDay ? day.expectedHours : 0), 0);
                        
                        onWorkingProfileChange({
                          ...workingProfile,
                          standardHoursPerWeek: newWeeklyTotal,
                          workingDays: updatedWorkingDays,
                        });
                      }}
                      onKeyDown={(e) => {
                        // Apply changes on Enter key
                        if (e.key === 'Enter') {
                          const value = parseFloat(dailyHoursInputs[dayKey]) || 0;
                          const clampedValue = Math.max(0, Math.min(24, value));
                          
                          const updatedWorkingDays = {
                            ...workingProfile.workingDays,
                            [dayKey]: {
                              ...dayData,
                              expectedHours: clampedValue,
                              isCustom: true,
                            },
                          };
                          
                          const newWeeklyTotal = Object.values(updatedWorkingDays)
                            .reduce((sum, day) => sum + (day.isWorkingDay ? day.expectedHours : 0), 0);
                          
                          onWorkingProfileChange({
                            ...workingProfile,
                            standardHoursPerWeek: newWeeklyTotal,
                            workingDays: updatedWorkingDays,
                          });
                          
                          e.currentTarget.blur();
                        }
                      }}
                      disabled={!dayData.isWorkingDay}
                      className="h-9 text-sm text-center"
                      min="0"
                      step="0.01"
                    />
                    <div className="text-xs text-center text-muted-foreground">
                      {dayData.isWorkingDay ? `${dayData.expectedHours.toFixed(2)}h` : '-'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Policy Checks Section - Main Heading */}
        <div className="flex items-start gap-3 pb-4 border-b mt-8">
          <div className="rounded-lg bg-primary/10 p-2.5">
            <ShieldAlert className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold">Policy Checks</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Configure alerts for Policy violations
            </p>
          </div>
        </div>

        {/* Policy Checks Content */}
        <div className="space-y-3">

          {/* Monitoring Frequency */}
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border">
            <Label className="text-sm flex-shrink-0 w-32">Check Hours by</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="monitoringFrequency"
                  value="day"
                  checked={policyEnforcement.monitoringFrequency === 'day'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onPolicyEnforcementChange({
                        ...policyEnforcement,
                        monitoringFrequency: 'day'
                      });
                    }
                  }}
                  className="w-4 h-4 text-primary focus:ring-primary"
                />
                <span className="text-sm">Day</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="monitoringFrequency"
                  value="week"
                  checked={policyEnforcement.monitoringFrequency === 'week'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onPolicyEnforcementChange({
                        ...policyEnforcement,
                        monitoringFrequency: 'week'
                      });
                    }
                  }}
                  className="w-4 h-4 text-primary focus:ring-primary"
                />
                <span className="text-sm">Week</span>
              </label>
            </div>
          </div>

          {/* Policy Check Cards */}
          <div className="space-y-4">
            <PolicyCheckCard
              title="Excessive Hours Alert"
              config={excessiveHoursConfig}
              onChange={(config: PolicyCheckConfig) => {
                setExcessiveHoursConfig(config);
                // TODO: Persist to backend or parent state if needed
                console.log('Excessive Hours config changed:', config);
              }}
              monitoringFrequency={policyEnforcement.monitoringFrequency}
              expectedHoursPerWeek={workingProfile.standardHoursPerWeek}
              dailyHours={{
                monday: workingProfile.workingDays.monday.expectedHours,
                tuesday: workingProfile.workingDays.tuesday.expectedHours,
                wednesday: workingProfile.workingDays.wednesday.expectedHours,
                thursday: workingProfile.workingDays.thursday.expectedHours,
                friday: workingProfile.workingDays.friday.expectedHours,
                saturday: workingProfile.workingDays.saturday.expectedHours,
                sunday: workingProfile.workingDays.sunday.expectedHours,
              }}
            />

            <PolicyCheckCard
              title="Missing Hours Alert"
              config={missingHoursConfig}
              onChange={(config: PolicyCheckConfig) => {
                setMissingHoursConfig(config);
                // TODO: Persist to backend or parent state if needed
                console.log('Missing Hours config changed:', config);
              }}
              monitoringFrequency={policyEnforcement.monitoringFrequency}
              expectedHoursPerWeek={workingProfile.standardHoursPerWeek}
              dailyHours={{
                monday: workingProfile.workingDays.monday.expectedHours,
                tuesday: workingProfile.workingDays.tuesday.expectedHours,
                wednesday: workingProfile.workingDays.wednesday.expectedHours,
                thursday: workingProfile.workingDays.thursday.expectedHours,
                friday: workingProfile.workingDays.friday.expectedHours,
                saturday: workingProfile.workingDays.saturday.expectedHours,
                sunday: workingProfile.workingDays.sunday.expectedHours,
              }}
              isMissingHours={true}
            />

            <PolicyCheckCard
              title="Non-Working Day Entry"
              config={nonWorkingDayConfig}
              onChange={(config: PolicyCheckConfig) => {
                setNonWorkingDayConfig(config);
                // TODO: Persist to backend or parent state if needed
                console.log('Non-Working Day config changed:', config);
              }}
              monitoringFrequency={policyEnforcement.monitoringFrequency}
              expectedHoursPerWeek={workingProfile.standardHoursPerWeek}
              dailyHours={{
                monday: workingProfile.workingDays.monday.expectedHours,
                tuesday: workingProfile.workingDays.tuesday.expectedHours,
                wednesday: workingProfile.workingDays.wednesday.expectedHours,
                thursday: workingProfile.workingDays.thursday.expectedHours,
                friday: workingProfile.workingDays.friday.expectedHours,
                saturday: workingProfile.workingDays.saturday.expectedHours,
                sunday: workingProfile.workingDays.sunday.expectedHours,
              }}
              isNonWorkingDay={true}
            />

            {/* Divider between soft checks and legal maximum */}
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">
                  Legal Maximum
                </span>
              </div>
            </div>

            <PolicyCheckCard
              title="Legal Maximum (Optional)"
              config={legalMaximumConfig}
              onChange={(config: PolicyCheckConfig) => {
                setLegalMaximumConfig(config);
                // TODO: Persist to backend or parent state if needed
                console.log('Legal Maximum config changed:', config);
              }}
              monitoringFrequency={policyEnforcement.monitoringFrequency}
              expectedHoursPerWeek={workingProfile.standardHoursPerWeek}
              dailyHours={{
                monday: workingProfile.workingDays.monday.expectedHours,
                tuesday: workingProfile.workingDays.tuesday.expectedHours,
                wednesday: workingProfile.workingDays.wednesday.expectedHours,
                thursday: workingProfile.workingDays.thursday.expectedHours,
                friday: workingProfile.workingDays.friday.expectedHours,
                saturday: workingProfile.workingDays.saturday.expectedHours,
                sunday: workingProfile.workingDays.sunday.expectedHours,
              }}
              isLegalMaximum={true}
            />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
